import { Injectable } from "@angular/core";
import { DataBaseService } from "../../../core/db/db.service";
import { Observable } from "rxjs";
import { AnalyticsService } from "../../../core/analytics/analytics.service";
import { RxApiService } from "../../../core/communication/http/rx-api.service";
import { map as rxMap } from "rxjs/operators";
import { AdminStats, EmailTargetEntry, Issue } from "../../../core/types";
import {BillingResponse, Plan, UpcomingInvoice} from "../../../core/types/billing";

@Injectable()
export class OrganizationService {
    private boundServerFetcher: () => Observable<any>;

    constructor(
        private rxApi: RxApiService,
        private db: DataBaseService,
        private analytics: AnalyticsService
    ) {
        this.boundServerFetcher = this.fetchDashboardFromServer.bind(this);
    }

    fetchDashboardFromDb(): Promise<any> {
        const dashboardData: any = {};

        return this.db
            .transaction("r?", "users", table => table.toArray())
            .then(result => (dashboardData.users = result || []))
            .then(() => dashboardData);
    }

    fetchDashboardFromServer(): Observable<any> {
        return this.rxApi.get(["org", "dashboard"]);
    }

    devicesUsage(qs?: Record<string, any>): Observable<any> {
        return this.rxApi.get(["org", "usage"], qs);
    }

    dashboard(): Observable<any> {
        return new Observable<any>(observable => {
            this.fetchDashboardFromDb()
                .then((result: any) => result && observable.next(result))
                .catch()
                .then(() =>
                    this.boundServerFetcher().subscribe((result: any) => {
                        observable.next(result);

                        if (result) {
                            this.db
                                .transaction("rw?", "users", table => {
                                    return table
                                        .clear()
                                        .then(() => table.bulkPut(result.users || []));
                                })
                                .then(() => observable.complete(), () => observable.complete())
                                .catch(() => observable.complete());
                        } else {
                            observable.complete();
                        }
                    })
                );
        });
    }

    activity(qs?: any): Observable<any> {
        return this.rxApi.get(["org", "activity"], qs);
    }

    downloadActivity(): void {
        this.rxApi.download(["org", "activity", "download"], "activity_stream_dump.txt");
    }

    billing(qs?: any): Observable<BillingResponse> {
        return this.rxApi.get(["org", "billing"], qs);
    }

    billingInvoices(): Observable<any> {
        return this.rxApi.get(["org", "billing", "invoices"]);
    }

    billingInvoiceDownloadPDF(invoiceID: string): void {
        this.rxApi.download(["org", "billing", "invoices", invoiceID, "download"], "Invoice.pdf");
    }

    billingNotifyQuota(data: any): Observable<any> {
        return this.rxApi.post(["org", "billing", "notify", "quota"], {}, data);
    }

    billingCreate(data: any): Observable<any> {
        return this.rxApi.post(["org", "billing"], {}, data).pipe(
            rxMap(result => {
                if (data.plan) {
                    this.analytics.handle("organization.plan_create", { value: data.plan.amount });
                }

                return result;
            })
        );
    }

    billingUpdate(data: any): Observable<any> {
        return this.rxApi.patch(["org", "billing"], {}, data).pipe(
            rxMap(result => {
                if (data.plan) {
                    this.analytics.handle("organization.plan_update", { value: data.plan.amount });
                }

                return result;
            })
        );
    }

    billingCancel(): Observable<any> {
        return this.rxApi.remove(["org", "billing"]);
    }

    billingSetEmails(emailsList: EmailTargetEntry[]): Observable<any> {
        return this.rxApi.post(["org", "billing", "emails"], {}, { emails: emailsList });
    }

    billingSetupSource(data: any): Observable<any> {
        return this.rxApi.post(["org", "billing", "setup_source"], {}, data);
    }

    list(qs?: any): Observable<any> {
        return this.rxApi.get(["orgs"], qs);
    }

    upcomingInvoices(qs?: any): Observable<UpcomingInvoice[]> {
        return this.rxApi.get(["orgs", "invoices"], qs);
    }

    get(): Observable<any> {
        return this.rxApi.get(["org"]);
    }

    updateCurrent(org: any): Observable<any> {
        return this.rxApi.patch(["org"], {}, org);
    }

    update(orgId: string, org: any): Observable<any> {
        return this.rxApi.patch(["orgs", orgId], {}, org);
    }

    remove(orgId: string): Observable<any> {
        return this.rxApi.remove(["orgs", orgId]);
    }

    detachFromStripe(orgId: string): Observable<any> {
        return this.rxApi.post(["orgs", orgId, "billing", "detach"], {}, null);
    }

    listStrategies(): Observable<any> {
        return this.rxApi.get(["orgs", "billing", "strategies"]);
    }

    resendInvoiceMail(invoiceId: string): Observable<any> {
        return this.rxApi.post(["org", "billing", "invoices", invoiceId, "resend"]);
    }

    previewInvoice(orgId: string, plan: Plan): Observable<any> {
        return this.rxApi.post(["org", "billing", "preview"], {}, { orgId, plan });
    }

    processEncryptedIssues(orgId: string): Observable<any> {
        return this.rxApi.post(["org", orgId, "process", "encrypted"]);
    }

    // We need this method here to decouple 'Integrations' module from
    // the rest of the app
    public pushIntegration(
        appId: string,
        issueId: string,
        provider: string,
        id: string
    ): Observable<any> {
        return this.rxApi.patch(
            ["apps", appId, "issues", issueId, "integrations"],
            {},
            { id: id, provider: provider }
        );
    }

    public clearIntegrations(appId: string, issueId: string, ids: string[]): Observable<any> {
        return this.rxApi.patch(
            ["apps", appId, "issues", issueId, "integrations", "clear"],
            {},
            { ids }
        );
    }

    public pushIntegrationsBulk(
        appId: string,
        provider: string,
        id: string,
        issues: Pick<Issue, '_id' | 'key'>[]
    ): Observable<any> {
        const body = {
            issues,
            options: { id, provider }
        };

        return this.rxApi.patch(["apps", appId, "issues", "integrations", "_bulk"], {}, body);
    }

    public adminStats(): Observable<AdminStats> {
        return this.rxApi.get(["orgs", "admstats"]);
    }

    public applicationsStatistics(filters?: Record<string, any>): Observable<any> {
        return this.rxApi.post(
            ["org", "statistics"], null,
            filters
        );
    }
}
